import { useEffect, useState } from "react";
import { toast } from "sonner";
import { House, CaretDown, CaretUp, IdentificationCard } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";
import { formatET } from "../lib/dateUtils";

const STAGES = ["Inquiry", "In Review", "Documents Sent", "Active Trust"];
const navItems = [
  { to: "/admin/trust", label: "Trust Pipeline", icon: <House size={18} weight="duotone" />, testId: "nav-admin-trust" },
  { to: "/admin/trust-control-panel", label: "Trust Control Panel", icon: <IdentificationCard size={18} weight="duotone" />, testId: "nav-admin-trust-control-panel" },
];

export default function AdminTrustPage() {
  const confirmEdit = useEditConfirm();
  const [records, setRecords] = useState([]);
  const [notesDraft, setNotesDraft] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [expandedRaw, setExpandedRaw] = useState([]);

  const fetchAll = async () => {
    const { data } = await api.get("/admin/trust");
    setRecords(data);
  };

  const fetchSubmissions = async () => {
    try {
      const { data } = await api.get("/admin/trust-intake-submissions");
      setSubmissions(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleRaw = (id) => {
    setExpandedRaw((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  useEffect(() => {
    fetchAll();
    fetchSubmissions();
  }, []);

  const updateStage = async (id, stage) => {
    const r = records.find((x) => x.id === id);
    if (!(await confirmEdit(`${r?.store_display_name || "this record"}'s stage`))) return;
    try {
      await api.patch(`/admin/trust/${id}`, { stage });
      toast.success("Stage updated");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveNotes = async (id, currentNotes) => {
    const r = records.find((x) => x.id === id);
    if (!(await confirmEdit(`${r?.store_display_name || "this record"}'s notes`))) return;
    try {
      const value = notesDraft[id] !== undefined ? notesDraft[id] : currentNotes;
      await api.patch(`/admin/trust/${id}`, { admin_notes: value });
      toast.success("Notes saved");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Trust & Asset Protection" moduleBackPath="/admin">
      <div data-testid="admin-trust-page">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-8" title="Every trust and asset protection inquiry from a client store, plus live intake submissions from the public trust website.">Trust & Asset Protection Pipeline</h1>

        <div className="mb-10" data-testid="trust-intake-webhook-section">
          <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-1" title="These arrive automatically from the public trust intake form on the website — no manual entry needed.">Trust Intake Submissions (Website Webhook)</h2>
          <p className="text-igc-muted text-xs mb-4">Live submissions from the public trust intake form — arrives here automatically the moment someone submits.</p>
          {submissions.length === 0 && <p className="text-igc-muted text-sm mb-6" data-testid="trust-intake-empty">No website intake submissions received yet.</p>}
          <div className="space-y-3 mb-6">
            {submissions.map((s) => (
              <div key={s.id} className="bg-igc-surface border border-igc-gold/15 p-5" data-testid={`trust-intake-submission-${s.id}`}>
                <div className="flex items-center justify-between gap-4 flex-wrap mb-1">
                  <p className="text-igc-cream text-sm">{s.name || "(No name provided)"} <span className="text-igc-muted text-xs">{s.email && `— ${s.email}`} {s.phone && `— ${s.phone}`}</span></p>
                  <p className="text-igc-muted text-xs">{formatET(s.received_at, "MMM d, yyyy h:mm a")}</p>
                </div>
                {s.message && <p className="text-igc-muted text-sm mt-1">{s.message}</p>}
                <button data-testid={`trust-intake-raw-toggle-${s.id}`} onClick={() => toggleRaw(s.id)} title="Shows or hides the exact raw JSON data this submission sent, for debugging." className="flex items-center gap-1 text-xs text-igc-goldText hover:text-igc-goldHover mt-2">
                  {expandedRaw.includes(s.id) ? <CaretUp size={12} /> : <CaretDown size={12} />} Raw Data
                </button>
                {expandedRaw.includes(s.id) && (
                  <pre className="bg-igc-surfaceLight text-igc-muted text-xs p-3 mt-2 whitespace-pre-wrap overflow-x-auto" data-testid={`trust-intake-raw-${s.id}`}>
                    {JSON.stringify(s.raw_payload, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </div>

        <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-4" title="Every client store's trust/asset protection deal, tracked from initial inquiry to active trust.">Client Trust Pipeline</h2>
        {records.length === 0 && <p className="text-igc-muted text-sm">No trust inquiries yet.</p>}
        <div className="space-y-4">
          {records.map((r) => (
            <div key={r.id} className="bg-igc-surface border border-igc-gold/15 p-6" data-testid={`trust-record-${r.id}`}>
              <div className="flex items-center justify-between mb-4">
                <p className="font-serif text-lg text-igc-cream">{r.store_display_name}</p>
                <Select value={r.stage} onValueChange={(v) => updateStage(r.id, v)}>
                  <SelectTrigger data-testid={`trust-stage-select-${r.id}`} title="Where this deal is at — from initial inquiry through active trust." className="w-56 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    {STAGES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-igc-muted text-xs mb-3">Updated {formatET(r.updated_at, "MMM d, yyyy h:mm a")}</p>
              <Textarea
                data-testid={`trust-notes-input-${r.id}`}
                defaultValue={r.admin_notes}
                onChange={(e) => setNotesDraft({ ...notesDraft, [r.id]: e.target.value })}
                placeholder="Internal notes (admin-only)..."
                title="Private notes about this deal, visible only to admins."
                className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream mb-3 min-h-20"
              />
              <Button data-testid={`trust-notes-save-${r.id}`} onClick={() => saveNotes(r.id, r.admin_notes)} title="Saves the note you just typed." className="bg-transparent border border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black">
                Save Notes
              </Button>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
